import {clone, nnTrim} from "../util";
import {Base} from "../Base";

const COPYRIGHT_NOTICE_TEXT = '(c) European Union, {0}, CC BY 4.0';

const MERCATOR_CODE = "EPSG:900913";
const WGS84_CODE = "EPSG:4326";

/**
 * The list of resolutions has the possible resolutions for every possible projection
 * @type {Object<string,Array<number>>}
 */
const RESOLUTIONS = {};
RESOLUTIONS[WGS84_CODE] = [5.625, 2.8125, 1.40625, 0.703125, 0.3515625, 0.17578125, 0.087890625, 0.0439453125, 0.02197265625, 0.010986328125, 0.0054931640625, 0.00274658203125, 0.001373291015625, 0.0006866455078125, 0.0003433227539062, 0.0001716613769531, 0.0000858306884766, 0.0000429153442383, 0.0000214576721191, 0.0000107288360596, 0.0000053644180298];
RESOLUTIONS[MERCATOR_CODE] = [626172.135625, 313086.0678125, 156543.03390625, 78271.516953125, 39135.7584765625, 19567.87923828125, 9783.939619140625, 4891.9698095703125, 2445.9849047851562, 1222.9924523925781, 611.4962261962891, 305.74811309814453, 152.87405654907226, 76.43702827453613, 38.218514137268066, 19.109257068634033, 9.554628534317017, 4.777314267158508, 2.388657133579254, 1.194328566789627, 0.5971642833948135];
RESOLUTIONS["EPSG:3035"] = [626172.135625, 313086.0678125, 156543.03390625, 78271.516953125, 39135.7584765625, 19567.87923828125, 9783.939619140625, 4891.9698095703125, 2445.9849047851562, 1222.9924523925781, 611.4962261962891, 305.74811309814453, 152.87405654907226, 76.43702827453613, 38.218514137268066, 19.109257068634033, 9.554628534317017, 4.777314267158508, 2.388657133579254, 1.194328566789627, 0.5971642833948135];
const TILE_IMAGE_SIZE = 1024;
const TILE_IMAGE_SIZE_MOBILE = 512;

const MAX_SMALL_HEIGHT_THRESHOLD = 680;
const MAX_SMALL_WIDTH_THRESHOLD = 950;

const BING_MAPS_KEY = 'Ai7d83ncnmP99Uiy5RxVZwXUPmoypR3N-6QWv5FK1hB5DrKquIBJ0JvMhnVi3Ld_';

/**
 * Verify coordinates
 * @param coordinate {Array<number>} The coordinate to check
 * @param allowLonWrap If true allows lon values <-180 and >180
 * @param bbox {Array<number>} the bounding box. If present checks that the coordinate is inside it
 * @return {boolean} true if valid
 */
function verifyCoordinateLonLatWgs84(coordinate, bbox = undefined, allowLonWrap = false) {
    let lon = coordinate[0];
    if (!allowLonWrap && (lon < -180 || lon > 180)) {
        return false;
    }
    let lat = coordinate[1];
    if (lat < -90 || lat > 90) {
        return false;
    }
    if (bbox) {
        if (lon < bbox[0] || lon > bbox[2]) {
            return false;
        }
        if (lat < bbox[1] || lat > bbox[3]) {
            return false;
        }
    }
    return true;
}

/**
 * Verify a bounding box
 * @param extent {Array<number>} the bounding box
 * @param allowLonWrap If true allows lon values <-180 and >180
 * @return {boolean} true if valid
 */
function verifyExtentLonLatWgs84(extent, allowLonWrap = false) {
    let lonMin = extent[0];
    let latMin = extent[1];
    let lonMax = extent[2];
    let latMax = extent[3];
    return lonMax > lonMin && latMax > latMin
        && verifyCoordinateLonLatWgs84([lonMin, latMin], undefined, allowLonWrap)
        && verifyCoordinateLonLatWgs84([lonMax, latMax], undefined, allowLonWrap);
}


/**
 * Verify a url
 * @param url string the url
 * @return {boolean} true if valid
 */
function verifyUrl(url) {
    url = url.trim();
    return url.indexOf("http://") === 0
        || url.indexOf("https://") === 0
        || url.indexOf("//") === 0;
}


/**
 * Any configuration
 */
class Config extends Base {

    /**
     *
     * @param config {Object} The config object
     */
    constructor(config) {
        super();
        this.config = config;
    }

    /**
     * Override this config
     * @param runtimeConfig {RuntimeConfig} The configuration override
     */
    override(runtimeConfig) {
    }

    /**
     * Verify this configuration and return a list of errors
     * @return {Array<string>}
     */
    verify() {
        return [];
    }

    /**
     * Get this config object
     * @return {Object}
     */
    getConfig() {
        return this.config;
    }

    clone() {
        return new this.__proto__.constructor(clone(this.config));
    }
}

/**
 * A configuration that has a code
 * @extends Config
 * @hideconstructor
 */
class CodeConfig extends Config {

    static _CODES_BY_CLASS = {}

    constructor(config) {
        super(config);
        if (!CodeConfig._CODES_BY_CLASS[this.getClass()])
            CodeConfig._CODES_BY_CLASS[this.getClass()] = {}
        CodeConfig._CODES_BY_CLASS[this.getClass()][config.code] = CodeConfig._CODES_BY_CLASS[this.getClass()][config.code]
            ? CodeConfig._CODES_BY_CLASS[this.getClass()][config.code] + 1
            : 1;
    }

    /**
     * Config code
     * @return {string}
     */
    getCode() {
        return this.config.code;
    }

    getLogId() {
        return nnTrim(this.getClassName(), this.getClass()) + " [" + this.getCode() + "]";
    }

    verify() {
        let errors = super.verify();
        if (!this.getCode() || this.getCode().length === 0) {
            errors.push(this.getLogId() + ": code is mandatory");
        } else if (CodeConfig._CODES_BY_CLASS[this.getClass()][this.getCode()] > 1) {
            errors.push(this.getLogId() + ": duplicated code");
        }
        return errors;
    }
}

/**
 * A configuration that has a unique code (for the same type) and a name
 * @extends CodeConfig
 * @hideconstructor
 */
class CodeNameConfig extends CodeConfig {

    /**
     * The name of this config
     * @return {string}
     */
    getName() {
        return this.config.name;
    }

    verify() {
        let errors = super.verify(this);
        const nameError = this.verifyName(this.getName());
        if(nameError)
            errors.push(this.getLogId()+": "+nameError)
        return errors;
    }

    verifyName(name) {
        if (!nnTrim(name)) {
            return "name is mandatory";
        }
    }
}

/**
 * A configuration that has a unique code (for the same type), a name and an info description
 * @extends CodeNameConfig
 * @hideconstructor
 */
class CodeNameInfoConfig extends CodeNameConfig {

    /**
     *
     * @return {string}
     */
    getInfo() {
        return this.config.info;
    }
}

/**
 * A configuration that has a unique code
 * @extends Config
 * @hideconstructor
 */
class CodeUniqueConfig extends Config {
    static _ALL_CODES = {}

    constructor(config) {
        super(config);
        CodeUniqueConfig._ALL_CODES[config.code] = CodeUniqueConfig._ALL_CODES[config.code]
            ? CodeUniqueConfig._ALL_CODES[config.code] + 1
            : 1;
    }

    /**
     * Config code
     * @return {string}
     */
    getCode() {
        return this.config.code;
    }

    getLogId() {
        return nnTrim(this.getClassName(), this.getClass()) + " [" + this.getCode() + "]";
    }

    verify() {
        let errors = super.verify();

        if (!this.getCode() || this.getCode().length === 0) {
            errors.push(this.getLogId() + ": code is mandatory");
        } else if (CodeUniqueConfig._ALL_CODES[this.getCode()] > 1) {
            errors.push(this.getLogId() + ": duplicated code");
        }
        return errors;
    }
}

/**
 * A configuration that has a unique code and a name
 * @extends CodeUniqueConfig
 * @hideconstructor
 */
class CodeUniqueNameConfig extends CodeUniqueConfig {

    /**
     * The name of this config
     * @return {string}
     */
    getName() {
        return this.config.name;
    }

    verify() {
        let errors = super.verify(this);
        if (!this.getName() || this.getName().length === 0) {
            errors.push(this.getLogId() + ": name is mandatory");
        }
        return errors;
    }
}

/**
 * A configuration that has a unique code, a name and an info description
 * @extends CodeUniqueNameConfig
 * @hideconstructor
 */
class CodeUniqueNameInfoConfig extends CodeUniqueNameConfig {

    /**
     *
     * @return {string}
     */
    getInfo() {
        return this.config.info;
    }
}


export {
    Config,
    CodeConfig,
    CodeNameConfig,
    CodeNameInfoConfig,
    CodeUniqueConfig,
    CodeUniqueNameConfig,
    CodeUniqueNameInfoConfig,
    COPYRIGHT_NOTICE_TEXT,
    RESOLUTIONS,
    TILE_IMAGE_SIZE,
    TILE_IMAGE_SIZE_MOBILE,
    MAX_SMALL_HEIGHT_THRESHOLD,
    MAX_SMALL_WIDTH_THRESHOLD,
    BING_MAPS_KEY,
    WGS84_CODE,
    MERCATOR_CODE,
    verifyExtentLonLatWgs84,
    verifyCoordinateLonLatWgs84,
    verifyUrl
}